import { ChevronLeft, ChevronRight, Pause, Play } from "lucide-react";
import {
  type PointerEvent,
  type Ref,
  useEffect,
  useRef,
  useState,
} from "react";
import { AuctionImage } from "./auction-image.tsx";
import { useAuctionAutoplay } from "./use-auction-autoplay";
import { useAuctionVisibility } from "./use-auction-visibility";

type FilmstripProps = {
  photos: string[];
  name: string;
  selected: number;
  paused?: boolean;
  photoButtonRef: Ref<HTMLButtonElement>;
  onSelect: (index: number) => void;
  onExpand: (index: number) => void;
};

export function AuctionPhotoFilmstrip({
  photos,
  name,
  selected,
  paused = false,
  photoButtonRef,
  onSelect,
  onExpand,
}: FilmstripProps) {
  const stripRef = useRef<HTMLDivElement>(null);
  const keyboardPhoto = useRef<HTMLButtonElement | null>(null);
  const thumbnailRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const [reducedMotion, setReducedMotion] = useState(true);
  const [hovered, setHovered] = useState(false);
  const [keyboardFocused, setKeyboardFocused] = useState(false);
  const [userPaused, setUserPaused] = useState(false);
  const [dragging, setDragging] = useState(false);
  const { ref: galleryRef, visible } = useAuctionVisibility<HTMLElement>();
  const [controlsActive, setControlsActive] = useState(false);
  const [touchControls, setTouchControls] = useState(true);
  const hideControlsTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const controlsVisible = touchControls || controlsActive || keyboardFocused;
  const controlsFade = `lockerverse-auction-gallery__control ${controlsVisible ? "is-visible" : ""}`;

  const scheduleControlsHide = () => {
    // biome-ignore lint/suspicious/noUnnecessaryConditions: The timer ref is assigned after pointer activity.
    if (hideControlsTimer.current) {
      clearTimeout(hideControlsTimer.current);
    }
    hideControlsTimer.current = setTimeout(
      () => setControlsActive(false),
      2000
    );
  };
  const showControls = (event: PointerEvent<HTMLDivElement>) => {
    if (event.pointerType !== "mouse") {
      setTouchControls(true);
      return;
    }
    setControlsActive(true);
    // biome-ignore lint/suspicious/noUnnecessaryConditions: The timer ref is assigned after pointer activity.
    if (hideControlsTimer.current) {
      clearTimeout(hideControlsTimer.current);
    }
    if (
      !(
        event.target instanceof Element &&
        event.target.closest("[data-gallery-control]")
      )
    ) {
      scheduleControlsHide();
    }
  };

  useEffect(() => {
    const preference = window.matchMedia("(hover: none), (pointer: coarse)");
    const update = () => setTouchControls(preference.matches);
    update();
    preference.addEventListener("change", update);
    return () => {
      preference.removeEventListener("change", update);
      clearTimeout(hideControlsTimer.current ?? undefined);
    };
  }, []);

  const autoplayEnabled =
    !(
      paused ||
      reducedMotion ||
      hovered ||
      keyboardFocused ||
      userPaused ||
      dragging
    ) && visible;
  const count = photos.length;
  const current = count ? ((selected % count) + count) % count : 0;
  const { viewportRef, embla, progressRef, freeze, reset } = useAuctionAutoplay(
    autoplayEnabled && count > 1,
    reducedMotion
  );

  useEffect(() => {
    const preference = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setReducedMotion(preference.matches);
    update();
    preference.addEventListener("change", update);
    return () => preference.removeEventListener("change", update);
  }, []);

  useEffect(() => {
    if (!embla) {
      return;
    }
    const select = () => onSelect(embla.selectedScrollSnap());
    embla.on("select", select).on("reInit", select);
    return () => {
      embla.off("select", select).off("reInit", select);
    };
  }, [embla, onSelect]);

  useEffect(() => {
    if (embla && embla.selectedScrollSnap() !== current) {
      embla.scrollTo(current, true);
    }
  }, [embla, current]);

  useEffect(() => {
    const updateVisibility = () => {
      if (document.hidden) {
        freeze();
      }
    };
    const release = () => setDragging(false);
    updateVisibility();
    document.addEventListener("visibilitychange", updateVisibility, true);
    window.addEventListener("pointerup", release);
    window.addEventListener("pointercancel", release);
    return () => {
      document.removeEventListener("visibilitychange", updateVisibility, true);
      window.removeEventListener("pointerup", release);
      window.removeEventListener("pointercancel", release);
    };
  }, [freeze]);

  const selectPhoto = (next: number) => {
    embla?.scrollTo((next + count) % count, reducedMotion);
    reset();
  };

  useEffect(() => {
    const previousPhoto = keyboardPhoto.current;
    keyboardPhoto.current = null;
    // biome-ignore lint/suspicious/noUnnecessaryConditions: Keyboard events assign this ref before the selected photo changes.
    if (previousPhoto && document.activeElement === previousPhoto) {
      galleryRef.current
        ?.querySelector<HTMLButtonElement>(
          '.lockerverse-auction-gallery__photo[tabindex="0"]'
        )
        ?.focus({ preventScroll: true });
    }
  }, [current, galleryRef]);

  useEffect(() => {
    const strip = stripRef.current;
    const thumbnail = thumbnailRefs.current[current];
    // biome-ignore lint/suspicious/noUnnecessaryConditions: React assigns the element ref after mounting.
    if (!(strip && thumbnail)) {
      return;
    }
    const stripBounds = strip.getBoundingClientRect();
    const photoBounds = thumbnail.getBoundingClientRect();
    if (photoBounds.left < stripBounds.left) {
      strip.scrollLeft -= stripBounds.left - photoBounds.left;
    } else if (photoBounds.right > stripBounds.right) {
      strip.scrollLeft += photoBounds.right - stripBounds.right;
    }
  }, [current, count]);

  return (
    // biome-ignore lint/a11y/noNoninteractiveElementInteractions: The gallery handles keys from its focusable photo and navigation buttons.
    <section
      aria-label={`${name} photos`}
      className="lockerverse-auction-gallery"
      onBlurCapture={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) {
          setKeyboardFocused(false);
        }
      }}
      onFocusCapture={(event) =>
        setKeyboardFocused(event.target.matches(":focus-visible"))
      }
      onKeyDown={(event) => {
        if (
          count < 2 ||
          event.altKey ||
          event.ctrlKey ||
          event.metaKey ||
          event.shiftKey
        ) {
          return;
        }
        if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
          event.preventDefault();
          if (
            event.target instanceof HTMLButtonElement &&
            event.target.classList.contains(
              "lockerverse-auction-gallery__photo"
            )
          ) {
            keyboardPhoto.current = event.target;
          }
          selectPhoto(current + (event.key === "ArrowRight" ? 1 : -1));
        }
      }}
      onPointerEnter={(event) => {
        if (event.pointerType === "mouse") {
          setHovered(true);
        }
      }}
      onPointerLeave={(event) => {
        if (event.pointerType === "mouse") {
          setHovered(false);
        }
      }}
      ref={galleryRef}
    >
      {count ? (
        <div
          className="lockerverse-auction-gallery__stage"
          onPointerEnter={showControls}
          onPointerLeave={scheduleControlsHide}
          onPointerMove={showControls}
        >
          <div
            className="lockerverse-auction-gallery__viewport"
            onPointerDownCapture={() => {
              freeze();
              setDragging(true);
            }}
            ref={viewportRef}
          >
            <div className="lockerverse-auction-gallery__track">
              {photos.map((photo, photoIndex) => (
                <div
                  aria-hidden={photoIndex !== current}
                  className="lockerverse-auction-gallery__slide"
                  key={`${photo}-${photoIndex}`}
                >
                  <button
                    aria-label={`View photo ${photoIndex + 1} of ${name} at full size`}
                    className="lockerverse-auction-gallery__photo"
                    onClick={() => {
                      onExpand(photoIndex);
                    }}
                    ref={photoIndex === current ? photoButtonRef : undefined}
                    tabIndex={photoIndex === current ? 0 : -1}
                    type="button"
                  >
                    <AuctionImage
                      alt={`${name}, ${photoIndex + 1} of ${count}`}
                      className="lockerverse-auction-gallery__image"
                      draggable={false}
                      height={900}
                      loading={
                        photoIndex === 0 ||
                        photoIndex === current ||
                        photoIndex === (current + 1) % count
                          ? "eager"
                          : "lazy"
                      }
                      maxWidth={1280}
                      sizes="(max-width: 1280px) 100vw, 45vw"
                      src={photo}
                      width={1200}
                    />
                  </button>
                </div>
              ))}
            </div>
          </div>
          {count > 1 && !reducedMotion && (
            <div className="lockerverse-auction-gallery__rotation">
              <div
                aria-hidden="true"
                className="lockerverse-auction-gallery__progress-track"
              >
                <div
                  className="lockerverse-auction-gallery__progress"
                  ref={progressRef}
                  style={{ transform: "scaleX(0)" }}
                />
              </div>
              <button
                aria-label={
                  userPaused ? "Play photo rotation" : "Pause photo rotation"
                }
                className={`lockerverse-auction-gallery__play ${controlsFade}`}
                data-gallery-control
                onClick={() => setUserPaused((value) => !value)}
                title={
                  userPaused ? "Play photo rotation" : "Pause photo rotation"
                }
                type="button"
              >
                {userPaused ? (
                  <Play aria-hidden="true" size={13} />
                ) : (
                  <Pause aria-hidden="true" size={13} />
                )}
              </button>
            </div>
          )}
          {count > 1 && (
            <>
              <button
                aria-label="Previous photo"
                className={`lockerverse-auction-gallery__previous ${controlsFade}`}
                data-gallery-control
                onClick={() => selectPhoto(current - 1)}
                type="button"
              >
                <ChevronLeft
                  aria-hidden="true"
                  className="lockerverse-auction-gallery__chevron"
                />
              </button>
              <button
                aria-label="Next photo"
                className={`lockerverse-auction-gallery__next ${controlsFade}`}
                data-gallery-control
                onClick={() => selectPhoto(current + 1)}
                type="button"
              >
                <ChevronRight
                  aria-hidden="true"
                  className="lockerverse-auction-gallery__chevron"
                />
              </button>
            </>
          )}
        </div>
      ) : (
        <div className="lockerverse-auction-gallery__empty">
          No photo available
        </div>
      )}
      {count > 1 && (
        <section
          aria-label="Choose a photo"
          className="lockerverse-auction-gallery__strip"
          ref={stripRef}
        >
          <div className="lockerverse-auction-gallery__thumbnails">
            {photos.map((photo, photoIndex) => (
              <button
                aria-label={`Show photo ${photoIndex + 1} of ${count}`}
                aria-pressed={current === photoIndex}
                className={`lockerverse-auction-gallery__thumbnail ${current === photoIndex ? "is-selected" : ""}`}
                key={`${photo}-${photoIndex}`}
                onClick={() => selectPhoto(photoIndex)}
                ref={(element) => {
                  thumbnailRefs.current[photoIndex] = element;
                }}
                type="button"
              >
                <AuctionImage
                  alt=""
                  className="lockerverse-auction-gallery__thumbnail-image"
                  height={66}
                  loading="lazy"
                  maxWidth={192}
                  sizes="88px"
                  src={photo}
                  width={88}
                />
              </button>
            ))}
          </div>
        </section>
      )}
    </section>
  );
}
